import { useUpdatePronosticV2Mutation } from "pronosticsv2/api/pronosticv2.api"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
    Alert,
    Anchor,
    Box,
    Breadcrumbs,
    Button,
    Card,
    Container,
    Group,
    LoadingOverlay,
    NumberInput,
    Radio,
    ScrollArea,
    TextInput,
    Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { Link, useNavigate } from "react-router-dom"
import { ADMIN_PATH } from "_urls"
import { z } from "zod"
import { v4 as uuidv4 } from "uuid"
import { PronosticV2 } from "pronosticsv2/types/pronosticv2.model"
import { useListPlayersQuery } from "quiz/api/players.endpoints"
import { GenericTable } from "../GenericTable"
import type { Player } from "quiz/types/player.model"
import Collapsable from "common/admin/ui/Collapsable"
import { Refresh } from "tabler-icons-react"
import { PlayerEditModal } from "pronosticsv2/admin/components/forms/PlayerEditForm"
// Import the refactored scoring functions
import { rankPlayersAndFormatWinners } from "../../../utils/scoringUtils" // Adjust path as needed

// Zod schema for sub-place validation
const subPlaceSchema = z.object({
    id: z.string(),
    title: z.string().min(1, "Title is required"),
    numberOfWinners: z.number().min(1, "Must have at least 1 winner"),
    status: z.enum(["enabled", "disabled"]),
    index: z.number().optional(),
})

type TableRow = {
    info: string
    pronos: string
    winner: string
    rank: string
    rawPlayer: Player
}

type Header<T> = {
    key: keyof T
    label: string
    sortable?: boolean
}

export function SubPlaceEditForm({
    pronosticV2,
    subPlaceTitle,
    subPlaceId,
    setOpened,
}: {
    pronosticV2: PronosticV2
    subPlaceTitle: string
    subPlaceId: string
    setOpened: (opened: boolean) => void
}) {
    const [updatePronosticV2] = useUpdatePronosticV2Mutation()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const navigate = useNavigate()
    const [opened2, setOpened2] = useState(false)
    const [selectedPlayerId, setSelectedPlayerId] = useState("")
    const [selectedPartitionId, setSelectedPartitionId] = useState<string>("")
    const [isWinnersLoading, setIsWinnersLoading] = useState(false)

    // Find the sub-place by ID
    const subPlaceToEdit = useMemo(() => {
        return pronosticV2.commonSettings.general.moduleSettings.place.subPlaces.find(
            (sp) => sp.id === subPlaceId
        )
    }, [pronosticV2, subPlaceId])

    const {
        data: players,
        isFetching,
        refetch: refetchPlayers,
    } = useListPlayersQuery({
        pronosticV2Id: pronosticV2.id,
        subPlaceId: subPlaceToEdit!.id,
    })

    const [selectedWinners, setSelectedWinners] = useState<string[]>([])

    useEffect(() => {
        if (!players?.length) return

        const subPlaceIndex = subPlaceToEdit?.id?.toString()
        const winnerInfo = pronosticV2.specificSettings.winnersInfo.find(
            (wi) => wi.subPlaceIndex === subPlaceIndex
        )

        if (winnerInfo) {
            const matchedPlayerIds = players
                .filter((player) =>
                    winnerInfo.winner.some(
                        (w) =>
                            w.id === player.id &&
                            w.partitionId === player.partitionId
                    )
                )
                .map((player) => player.id)

            setSelectedWinners(matchedPlayerIds)
        }
    }, [subPlaceToEdit, pronosticV2.specificSettings.winnersInfo, players])

    const headers: Header<TableRow>[] = [
        {
            key: "info",
            label: "Info",
            sortable: true,
        },
        {
            key: "pronos",
            label: "Pronos",
            sortable: true,
        },
        {
            key: "winner",
            label: "Winner",
            sortable: false,
        },
        {
            key: "rank",
            label: "Rank",
            sortable: true,
        },
    ]

    const tableData: TableRow[] = (players || []).map((player) => ({
        info: `${player.firstName} ${player.lastName},\n${player.email},\n${player.phoneNumber},\n${player.room},\n${player.enterprise}`,
        pronos:
            player?.pronosticInfo?.prediction
                ?.map((p) => p.values.join(" - "))
                .join(", ") || "No predictions",
        winner: player.id,
        rank: (() => {
            const winnerInfo = pronosticV2.specificSettings.winnersInfo.find(
                (wi) => wi.subPlaceIndex === subPlaceToEdit?.id?.toString()
            )
            const winner = winnerInfo?.winner?.find((w) => w.id === player.id)
            return winner?.rank?.toString() || "Not a winner"
        })(),
        rawPlayer: player,
    }))

    const handleWinnerSelect = (player: Player) => {
        const isSelected = selectedWinners.includes(player.id)

        if (isSelected) {
            setSelectedWinners(selectedWinners.filter((id) => id !== player.id))
        } else {
            if (selectedWinners.length >= subPlaceToEdit?.numberOfWinners!) {
                alert(
                    `You cannot select more than ${subPlaceToEdit?.numberOfWinners} winners`
                )
                return
            }
            setSelectedWinners([...selectedWinners, player.id])
        }
    }

    // Form setup
    const form = useForm({
        initialValues: subPlaceToEdit
            ? {
                  ...subPlaceToEdit,
                  id: subPlaceToEdit.id || uuidv4(),
              }
            : {
                  id: uuidv4(),
                  title: "",
                  numberOfWinners: 1,
                  status: "enabled" as const,
              },
        validate: (values) => {
            const result = subPlaceSchema.safeParse(values)
            if (!result.success) {
                return result.error.formErrors.fieldErrors
            }
            return {}
        },
    })

    const isDirty = useMemo(
        () => JSON.stringify(form.values) !== JSON.stringify(subPlaceToEdit),
        [form.values, subPlaceToEdit]
    )

    // Handle form submission
    const handleSave = useCallback(async () => {
        const validationResult = subPlaceSchema.safeParse(form.values)
        if (!validationResult.success) {
            const fieldErrors = validationResult.error.formErrors.fieldErrors
            Object.entries(fieldErrors).forEach(([field, messages]) => {
                form.setFieldError(field, messages?.join(", "))
            })
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const updatedSubPlaces =
                pronosticV2.commonSettings.general.moduleSettings.place.subPlaces.map(
                    (sp) =>
                        sp.title === subPlaceTitle
                            ? { ...form.values, index: sp.index }
                            : sp
                )

            const updatedPronosticV2: PronosticV2 = {
                ...pronosticV2,
                commonSettings: {
                    ...pronosticV2.commonSettings,
                    general: {
                        ...pronosticV2.commonSettings.general,
                        moduleSettings: {
                            ...pronosticV2.commonSettings.general
                                .moduleSettings,
                            place: {
                                ...pronosticV2.commonSettings.general
                                    .moduleSettings.place,
                                subPlaces: updatedSubPlaces,
                            },
                        },
                    },
                },
            }

            await updatePronosticV2(updatedPronosticV2).unwrap()
            setOpened(false)
            navigate(0)
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to update sub-place"
            )
        } finally {
            setIsLoading(false)
        }
    }, [
        form,
        pronosticV2,
        subPlaceTitle,
        updatePronosticV2,
        setOpened,
        navigate,
    ])

    const handlePlayerEditClose = () => {
        setOpened2(false)
        refetchPlayers()
    }

    const handlePlayerSaved = () => {
        refetchPlayers()
    }

    // Get the selected players
    const getSelectedPlayers = useCallback(() => {
        if (!players || !selectedWinners.length) return []
        return players.filter((player) => selectedWinners.includes(player.id))
    }, [players, selectedWinners])

    // ==============================================================================
    // REFACTORED: Use new scoring functions that match backend
    // ==============================================================================

    const handleSaveWinners = useCallback(async () => {
        const selectedPlayersWithPronosticInfo = getSelectedPlayers()
        const subPlaceIndex = subPlaceToEdit?.id?.toString() || ""
        const subPlaceTitle = subPlaceToEdit?.title

        // Use the refactored function that matches backend logic
        const winnersData = rankPlayersAndFormatWinners(
            selectedPlayersWithPronosticInfo,
            pronosticV2,
            subPlaceIndex,
            subPlaceTitle
        )

        setIsWinnersLoading(true)
        setError(null)

        try {
            const updatedPronosticV2: PronosticV2 = {
                ...pronosticV2,
                specificSettings: {
                    ...pronosticV2.specificSettings,
                    winnersInfo: [
                        ...pronosticV2.specificSettings.winnersInfo.filter(
                            (wi) => wi.subPlaceIndex !== subPlaceIndex
                        ),
                        winnersData,
                    ],
                },
            }

            await updatePronosticV2(updatedPronosticV2).unwrap()
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Failed to update winners"
            )
        } finally {
            setIsWinnersLoading(false)
        }
    }, [pronosticV2, subPlaceToEdit, updatePronosticV2, getSelectedPlayers])

    return (
        <>
            {isLoading && <LoadingOverlay visible overlayOpacity={0.8} />}
            <Box
                p="lg"
                sx={(theme) => ({
                    background:
                        theme.colorScheme === "dark"
                            ? theme.colors.gray[8]
                            : theme.colors.gray[2],
                    minHeight: "calc(100vh - 64px)",
                })}
            >
                <Container className="min-h-full">
                    <div
                        className="sticky top-0 z-50 pb-4"
                        style={{ backgroundColor: "#E9ECEF" }}
                    >
                        <Title order={3} align="center" m="xs">
                            Edit SubPlace: {subPlaceTitle}
                        </Title>
                        <Breadcrumbs separator="→">
                            <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                                Admin
                            </Anchor>
                            <Anchor
                                component={Link}
                                to={`/${ADMIN_PATH}/pronosticsv2`}
                            >
                                Pronostics
                            </Anchor>
                            <Anchor onClick={() => setOpened(false)}>
                                Pronostic Form
                            </Anchor>
                        </Breadcrumbs>
                        {error && (
                            <Alert color="red" mb="sm">
                                {error}
                            </Alert>
                        )}
                        <Group position="right">
                            <Button
                                disabled={!isDirty}
                                onClick={handleSave}
                                className="bg-button-admin disabled:opacity-75"
                                loading={isLoading}
                            >
                                Save
                            </Button>
                        </Group>
                    </div>
                    <ScrollArea h="calc(100vh - 145px)" type="never">
                        <Card shadow="sm" p="xl" className="overflow-visible">
                            <Group className="grid grid-cols-3 gap-4">
                                <TextInput
                                    label="Title"
                                    required
                                    {...form.getInputProps("title")}
                                    className="col-span-2"
                                />
                                <NumberInput
                                    label="No of Winners"
                                    min={1}
                                    {...form.getInputProps("numberOfWinners")}
                                />
                            </Group>
                            <Radio.Group
                                label="Status"
                                {...form.getInputProps("status")}
                                mt="md"
                            >
                                <Group mt="xs">
                                    <Radio value="enabled" label="Enabled" />
                                    <Radio value="disabled" label="Disabled" />
                                </Group>
                            </Radio.Group>
                        </Card>
                        <div className="my-10">
                            <Collapsable
                                title={
                                    tableData.length
                                        ? `Participants(${tableData.length})`
                                        : `Participants(Loading...)`
                                }
                            >
                                <Group position="right" mb="sm">
                                    <Button
                                        onClick={() => refetchPlayers()}
                                        variant="outline"
                                    >
                                        <Refresh />
                                    </Button>
                                    <Button
                                        onClick={handleSaveWinners}
                                        disabled={!selectedWinners.length}
                                        loading={isWinnersLoading}
                                        className="bg-button-admin disabled:opacity-75"
                                    >
                                        Update Winners
                                    </Button>
                                </Group>
                                {isFetching ? (
                                    <LoadingOverlay
                                        visible
                                        overlayOpacity={0}
                                    />
                                ) : (
                                    <GenericTable
                                        headers={headers}
                                        data={tableData}
                                        isWinner={(row) =>
                                            selectedWinners.includes(
                                                row.rawPlayer.id
                                            )
                                        }
                                        onWinnerClick={(row) =>
                                            handleWinnerSelect(row.rawPlayer)
                                        }
                                        onRowClick={(row) => {
                                            setSelectedPlayerId(
                                                row.rawPlayer.id
                                            )
                                            setSelectedPartitionId(
                                                row.rawPlayer.partitionId
                                            )
                                            setOpened2(true)
                                        }}
                                    />
                                )}
                            </Collapsable>
                        </div>
                    </ScrollArea>
                </Container>
            </Box>
            <PlayerEditModal
                playerId={selectedPlayerId}
                partitionId={selectedPartitionId}
                opened={opened2}
                setOpened={handlePlayerEditClose}
                pronostic={pronosticV2}
                predictionlength={
                    pronosticV2?.commonSettings.general.moduleSettings
                        .pronostics.length
                }
                onSave={handlePlayerSaved}
            />
        </>
    )
}
